import { 
    mdiCheck, 
    mdiEmailOutline, 
    mdiMagnify, 
    mdiPlus, 
    mdiSpeedometer,
    mdiTrendingUp,
    mdiLightningBolt,
    mdiAlertCircleOutline,
    mdiCircleOffOutline,
} from '@mdi/js';
import Icon from '@mdi/react';
import { format } from 'date-fns';
import _ from 'lodash';
import * as mobx from 'mobx';
import React from 'react';
import { Row } from 'react-table';
import { MessagePurposeType } from '../../api';
import { MessagingContext } from '../../services';
import { getOptions, Tooltip } from '../base';
import { TabContainer } from '../tab-container';
import {
    DataTableCell,
    IRowAction,
    ITableColumn,
    ITableFetchOptions,
} from '../data-table';
import { InfiniteScrollDataTablePresenter as ISDPresenter } from '../data-table/infinite-scroll-data-table.presenter';
import { FilterMethod, IFilterSchema, IColumnFiltersConfig } from '../data-table/components/filters/filters.models';
import { Settings } from '../data-table/components/settings/settings';
import { TableExport } from '../data-table/components/table-export/table-export';
import { AuthorDateFilter } from '../data-table/components/column-filter';

import * as S from './message-list.styles';
import { PendingButton } from './pending-button';
import { getNickname, IMessagingAuthor, IMessagingMessage, isLastAuthor } from './util';



const getInitials = (author: IMessagingAuthor | undefined): string => {
    if (!author) return '?';
    const nickname = getNickname(author);
    if (!nickname) return '?';
    const parts = nickname.split(' ').filter(Boolean);
    if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return nickname.substring(0, 2).toUpperCase();
};

interface IMessageListRedesignedProps {
    inboxCount: number;
    inboxMessages: IMessagingMessage[];
    doneCount: number;
    doneMessages: IMessagingMessage[];
    onFetchData: (newTableState: ITableFetchOptions) => Promise<void>;
    onShowCompose: () => void;
    selectedMessageId: string | undefined;
    showFiltering: boolean;
    showNavigation: boolean;
    showSettings?: boolean;
    customColumns?: ITableColumn[];
    unreadCount?: number;
    columnFiltersConfig?: IColumnFiltersConfig;
    rowActions?: IRowAction[];
}

interface IMessageListRedesignedState {
    tableState: ITableFetchOptions | undefined;
    selected: string[];
    showDoneMessages: boolean;
    isPending: boolean;
    searchTerm: string;
    lastKnownValidCount: number | null;
    isUnreadFilterActive: boolean;
    currentSortBy: any[];
    selectedRowId: string | null;
}

export class MessageListRedesigned extends React.Component<IMessageListRedesignedProps, IMessageListRedesignedState> {
    public static contextType = MessagingContext;
    private presenter: ISDPresenter<IMessagingMessage>;
    
    private get columnFiltersConfig(): IColumnFiltersConfig {
        return {
            ...(this.props.columnFiltersConfig || {}),
            ID: {
                filterType: 'String',
                filterId: 'filterId',
                placeholder: 'Search by ID...',
                defaultMethod: FilterMethod.LK,
            },
            Created: {
                filterType: 'Custom',
                component: AuthorDateFilter,
                filterId: 'filterCreatedCombined',
                label: 'Filter Created',
                defaultMethod: FilterMethod.LK,
            },
            Updated: {
                filterType: 'Custom',
                component: AuthorDateFilter,
                filterId: 'filterUpdatedCombined',
                label: 'Filter Updated',
                defaultMethod: FilterMethod.LK,
            }
        };
    }
    
    private get columns(): ITableColumn<IMessagingMessage>[] {
        return [
        {
            id: 'ID',
            Header: 'ID',
            accessor: 'id',
            minWidth: 100,
            maxWidth: 150,
            show: false,
        },
        {
            id: 'Created',
            Header: 'Created',
            accessor: 'created',
            Cell: ({ row, cell }): React.ReactElement => {
                const { author } = row.original;
                let formattedDate = '—';
                
                const dateValue = cell.value;
                if (dateValue) {
                    const parsedDate = new Date(dateValue);
                    if (!isNaN(parsedDate.getTime())) {
                        formattedDate = format(parsedDate, 'MMM d, yyyy, h:mm a');
                    }
                }
                
                return (
                    <S.FromCellContainer>
                        {this.renderAvatarField(author)}
                        <span>{formattedDate}</span>
                    </S.FromCellContainer>
                );
            },
            minWidth: 150,
            maxWidth: 250,
            show: true,
        },
        {
            id: 'Updated',
            Header: 'Updated',
            accessor: 'updated',
            Cell: ({ row, cell }): React.ReactElement => {
                const { lastAuthor } = row.original;
                let formattedDate = '—';
                
                const dateValue = cell.value;
                if (dateValue) {
                    const parsedDate = new Date(dateValue);
                    if (!isNaN(parsedDate.getTime())) {
                        formattedDate = format(parsedDate, 'MMM d, yyyy, h:mm a');
                    }
                }
                
                return (
                    <S.FromCellContainer>
                        {this.renderAvatarField(lastAuthor)}
                        <span>{formattedDate}</span>
                    </S.FromCellContainer>
                );
            },
            minWidth: 150,
            maxWidth: 250,
            defaultSort: { id: 'Updated', desc: true },
            show: true,
        },
        {
            id: 'Subject',
            Header: 'Subject',
            Cell: ({ row }): React.ReactElement => {
                const { documents, subject, read } = row.original;
                return (
                    <S.SubjectField unread={!read}>
                        {subject}
                        {!!documents && documents > 0 && <S.AttachmentField count={documents} />}
                    </S.SubjectField>
                );
            },
            accessor: 'subject',
            minWidth: 300,
            maxWidth: 1000,
        },
        {
            id: 'Priority',
            Header: 'Priority',
            accessor: (row) => {
                const priority = row.priority?.toUpperCase() || '';
                return priority || 'NONE';
            },
            Cell: ({ row }): React.ReactElement => {
                const priority = (row.original as any).priority;
                const priorityUpper = priority?.toUpperCase();
                
                let priorityIconPath: string = mdiCircleOffOutline;
                let priorityLabel = 'None';
                
                if (priorityUpper && priorityUpper !== '-') {
                    switch (priorityUpper) {
                        case 'LOW':
                            priorityIconPath = mdiSpeedometer;
                            priorityLabel = 'Low';
                            break;
                        case 'NORMAL':
                            priorityIconPath = mdiTrendingUp;
                            priorityLabel = 'Normal';
                            break;
                        case 'HIGH':
                            priorityIconPath = mdiLightningBolt;
                            priorityLabel = 'High';
                            break;
                        case 'URGENT':
                            priorityIconPath = mdiAlertCircleOutline;
                            priorityLabel = 'Urgent';
                            break;
                    }
                }
                
                return (
                    <S.PriorityTag priority={priorityUpper || ''}>
                        <S.PriorityTagIcon>
                            <Icon path={priorityIconPath} size={0.65} />
                        </S.PriorityTagIcon>
                        <S.PriorityTagLabel>{priorityLabel}</S.PriorityTagLabel>
                    </S.PriorityTag>
                );
            },
            minWidth: 90,
            maxWidth: 120,
            disableSortBy: true,
        },
        {
            id: 'Purpose',
            Header: 'Purpose',
            accessor: 'purposeType',
            Cell: DataTableCell('String', { normalized: true, type: 'String' }),
            minWidth: 180,
            show: false,
        },
        // @ts-expect-error Custom columns
        ...(this.props.customColumns || []),
        ];
    }

    private filterSchemas: IFilterSchema[] = [
        {
            label: 'ID',
            filterId: 'filterId',
            filterType: 'String',
            columnId: 'ID',
        },
        {
            label: 'Subject',
            filterId: 'filterSubject',
            filterType: 'String',
            columnId: 'Subject',
        },
        {
            label: 'Author',
            filterId: 'filterAuthor',
            filterType: 'String',
            columnId: 'Created',
        },
        {
            label: 'Updated',
            filterId: 'filterUpdated',
            filterType: 'DateTime',
            columnId: 'Updated',
        },
        {
            label: 'Created',
            filterId: 'filterCreated',
            filterType: 'DateTime',
            columnId: 'Created',
        },
        {
            label: 'Read',
            filterId: 'filterRead',
            filterType: 'Select',
            options: [
                {
                    label: 'Yes',
                    value: '1',
                },
                {
                    label: 'No',
                    value: '0',
                },
            ],
            columnId: '',
        },
        {
            label: 'Purpose',
            filterId: 'filterPurpose',
            filterType: 'Select',
            options: getOptions(MessagePurposeType, { normalize: true }),
            columnId: 'purpose',
        },
    ];

    constructor(props: IMessageListRedesignedProps) {
        super(props);
        const tableDefaults = this.getInitialTableDefaults();
        this.state = {
            tableState: undefined,
            selected: [],
            showDoneMessages: false,
            isPending: false,
            searchTerm: '',
            lastKnownValidCount: null,
            isUnreadFilterActive: false,
            currentSortBy: tableDefaults?.sortBy || [],
            selectedRowId: props.selectedMessageId || null,
        };
        this.presenter = new ISDPresenter<IMessagingMessage>({
            name: 'message-list-redesigned',
            data: [],
            columns: this.columns,
            filterSchemas: this.filterSchemas,
        });
    }

    private getInitialTableDefaults = () => {
        return new ISDPresenter<IMessagingMessage>({
            name: 'message-list-redesigned',
            data: [],
            columns: this.columns,
            filterSchemas: this.filterSchemas,
        }).getTableDefaults();
    };

    private handleFetchData = async (tableState: ITableFetchOptions): Promise<void> => {
        // Capture current sortBy before fetching
        if (tableState.sortBy) {
            this.setState({ currentSortBy: tableState.sortBy });
        }
        return this.props.onFetchData(tableState);
    };

    public componentDidUpdate(prevProps: IMessageListRedesignedProps, prevState: IMessageListRedesignedState): void {
        const { showDoneMessages, searchTerm, lastKnownValidCount } = this.state;
        const { doneCount, doneMessages, inboxCount, inboxMessages, selectedMessageId } = this.props;

        if (prevProps.selectedMessageId !== selectedMessageId) {
            this.setState({ selectedRowId: selectedMessageId || null });
        }

        const hasReadFilter = !!_.find(this.presenter.filters, { filterId: 'filterRead' });
        if (hasReadFilter !== this.state.isUnreadFilterActive) {
            this.setState({ isUnreadFilterActive: hasReadFilter });
        }

        // Reset accumulated data when switching tabs
        if (prevState.showDoneMessages !== showDoneMessages) {
            this.presenter.resetData();
            this.setState({ lastKnownValidCount: null });
            return;
        }

        const visibleMessages = showDoneMessages ? doneMessages : inboxMessages;
        const visibleCount = showDoneMessages ? doneCount : inboxCount;
        const prevVisibleCount = showDoneMessages ? prevProps.doneCount : prevProps.inboxCount;
        
        // Reset presenter when count changes (message added/removed/moved between tabs)
        // This ensures we don't append stale page 0 data on top of existing page 0 data
        if (visibleCount !== prevVisibleCount && this.presenter.accumulatedData.length > 0) {
            mobx.runInAction(() => {
                this.presenter.resetData();
            });
            this.setState({ lastKnownValidCount: null });
        }
        
        if (lastKnownValidCount !== null && visibleCount === lastKnownValidCount && this.presenter.accumulatedData.length === 0) {
            return;
        }
        
        const prevMessages = showDoneMessages ? prevProps.doneMessages : prevProps.inboxMessages;
        const messagesChanged = prevMessages !== visibleMessages;
        
        if (messagesChanged && visibleMessages.length > 0) {
            mobx.runInAction(() => {
                if (this.presenter.accumulatedData.length === 0) {
                    this.presenter.onDataUpdate(visibleMessages);
                    this.setState({ lastKnownValidCount: null });
                } else {
                    this.presenter.appendData(visibleMessages);
                }
            });
        } else if (messagesChanged && visibleMessages.length === 0) {
            mobx.runInAction(() => {
                this.presenter.resetData();
            });
            this.setState({ lastKnownValidCount: null });
        }
        
        const hasMore = this.presenter.accumulatedData.length < visibleCount;
        this.presenter.setHasMore(hasMore);
        this.presenter.onDataTotalChange(visibleCount);
    }

    public componentWillUnmount(): void {
        // Cancel pending debounced calls to prevent memory leaks
        this.updateSubjectFilter.cancel();
    }

    private renderCustomToolbar = (rows: Row<IMessagingMessage>[], prepareRow: (row: Row<IMessagingMessage>) => void): React.ReactElement => {
        const { selected, showDoneMessages, isPending, searchTerm } = this.state;
        const { doneCount, inboxCount, unreadCount } = this.props;
        const totalCount = showDoneMessages ? doneCount : inboxCount;

        return (
            <S.TableHeaderSection>
                <S.SearchSection>
                    <S.SearchInputWrapper>
                        <S.SearchIcon>
                            <Icon path={mdiMagnify} size={0.85} />
                        </S.SearchIcon>
                        <S.SearchInput 
                            type="text"
                            placeholder="Search messages"
                            value={searchTerm}
                            onChange={this.handleSearchChange}
                        />
                    </S.SearchInputWrapper>
                </S.SearchSection>

                <S.ToolbarActions>
                    <Settings
                        columns={this.columns}
                        hiddenColumnIds={this.presenter.hiddenColumnIds}
                        onColumnToggle={this.presenter.onColumnToggle}
                        variant="small"
                    />
                    
                    <TableExport 
                        rows={rows} 
                        prepareRow={prepareRow} 
                    />
                    
                    <S.FilterButtons>
                        {!!selected.length && !isPending && (
                            <>
                                {!showDoneMessages && (
                                    <S.InboxButton 
                                        active={false}
                                        onClick={this.markSelectedDone}>
                                        <Icon path={mdiCheck} size={0.75} />
                                        Mark {selected.length} as done
                                    </S.InboxButton>
                                )}
                                {showDoneMessages && (
                                    <S.InboxButton 
                                        active={false}
                                        onClick={this.markSelectedUndone}>
                                        Reopen {selected.length}
                                    </S.InboxButton>
                                )}
                            </>
                        )}
                        {isPending && <PendingButton />}
                    </S.FilterButtons>
                </S.ToolbarActions>
            </S.TableHeaderSection>
        );
    };

    public render(): React.ReactElement {
        const { showDoneMessages } = this.state;
        const {
            doneCount,
            inboxCount,
            onShowCompose,
        } = this.props;

        return (
            <S.MessageListContainer>
                <S.MessageHeader>
                    <S.HeaderTop>
                        <S.Title>Tasks</S.Title>
                        <S.HeaderActions>
                            <S.PrimaryButton 
                                disabled={(this.context as any).isReadOnly} 
                                onClick={onShowCompose}>
                                <Icon path={mdiPlus} size={0.7} />
                                <span>New task</span>
                            </S.PrimaryButton>
                        </S.HeaderActions>
                    </S.HeaderTop>
                </S.MessageHeader>

                {/* Table Card */}
                <S.TableCard>
                    <S.TableTabSection>
                        <TabContainer
                            tabs={[
                                { id: 'inbox', label: `Inbox (${inboxCount})`, icon: mdiEmailOutline },
                                { id: 'done', label: `Done (${doneCount})`, icon: mdiCheck }
                            ]}
                            activeTab={showDoneMessages ? 'done' : 'inbox'}
                            onTabChange={this.handleTabChange}
                        />
                    </S.TableTabSection>
                    
                    <S.MessagingTable
                        presenter={this.presenter}
                        isLoading={(this.context as any).isListLoading}
                        onFetch={this.handleFetchData}
                        onRowClick={this.onRowClick()}
                        rowClass={this.rowClass()}
                        hideFiltering={true}
                        hideNavigation={true}
                        hideSettings={true}
                        showExport={false}
                        selectableRows={true}
                        columnFiltersConfig={this.columnFiltersConfig}
                        renderToolbar={this.renderCustomToolbar}
                        rowActions={this.props.rowActions}
                        selectedRowId={this.state.selectedRowId}
                        onSelectedRowChange={(rowId) => this.setState({ selectedRowId: rowId })}
                    />
                </S.TableCard>
                
                <S.SelectedMessageRow show={!!this.props.selectedMessageId} />
            </S.MessageListContainer>
        );
    }

    private get hasFilter() {
        return !!_.find(this.presenter.filters, { filterId: 'filterRead' });
    }

    private rowClass(): (row: Row<IMessagingMessage>) => string {
        return (row: Row<IMessagingMessage>): string => {
            const classes: string[] = [];
            
            if (row.original.id === this.props.selectedMessageId) {
                classes.push('selected-message');
            }
            
            if (!row.original.read) {
                classes.push('unread-message');
            }
            
            return classes.join(' ');
        };
    }

    private onRowClick(): (row: Row) => Promise<void> {
        const { onMessageClose, onMessageOpen } = this.context as any;
        const { selectedMessageId } = this.props;
        return async (row: Row<IMessagingMessage>): Promise<void> => {
            const {
                original: { id },
            } = row;
            if (id === selectedMessageId) {
                await onMessageClose();
            } else {
                await (onMessageOpen && onMessageOpen(id));
            }
        };
    }

    private markSelectedDone = async (): Promise<void> => {
        this.setState({ isPending: true });
        if (_.includes(this.state.selected, this.props.selectedMessageId)) {
            await (this.context as any).onMessageClose();
        }
        await (this.context as any).onMarkAsClosed(this.state.selected);
        this.setState({
            isPending: false,
            selected: [],
        });
    };
    private markSelectedUndone = async (): Promise<void> => {
        this.setState({ isPending: true });
        await (this.context as any).onMarkAsOpen(this.state.selected);
        this.setState({
            isPending: false,
            selected: [],
        });
    };

    private isSelected(id: string): boolean {
        return _.includes(this.state.selected, id);
    }

    private toggleSelected(id: string): () => void {
        return (): void => {
            if (this.isSelected(id)) {
                this.setState({ selected: _.without(this.state.selected, id) });
            } else {
                this.setState({ selected: _.concat(this.state.selected, id) });
            }
        };
    }

    private handleTabChange = (tabId: string): void => {
        const newValue = tabId === 'done';
        if (newValue !== this.state.showDoneMessages) {
            this.setState({ showDoneMessages: newValue, selected: [] });
         
            this.presenter.resetData();

            const tableDefaults = this.presenter.getTableDefaults();
            this.props.onFetchData({
                pageIndex: 0,
                pageSize: tableDefaults?.pageSize || 100,
                sortBy: this.state.currentSortBy,
                validFilters: [
                    ...(this.presenter.validFilters || []),
                    {
                        filterId: 'filterOpen',
                        filterMethod: FilterMethod.EQ,
                        filterType: 'String',
                        value: newValue ? '0' : '1',
                    },
                ],
            });
        }
    };

    private updateSubjectFilter = _.debounce((value: string): void => {
        const currentCount = this.state.showDoneMessages ? this.props.doneCount : this.props.inboxCount;
        this.setState({ lastKnownValidCount: currentCount });
        
        if (value.trim()) {
            this.presenter.findAndUpdateFilter(
                { filterId: 'filterSubject' },
                {
                    filterId: 'filterSubject',
                    filterType: 'String',
                    filterMethod: FilterMethod.LK,
                    value: value.trim(),
                },
            );
        } else {
            this.presenter.findAndRemoveFilter({ filterId: 'filterSubject' });
        }
    }, 500);

    private handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const value = e.target.value;
        this.setState({ searchTerm: value });
        this.updateSubjectFilter(value);
    };


    private renderAvatarField(author: IMessagingAuthor | undefined): React.ReactElement {
        const initials = getInitials(author);
        const nickname = getNickname(author);
        
        return (
            <Tooltip direction="top" title={<span>{nickname || 'Unknown'}</span>}>
                <S.UserAvatar>
                    {initials}
                </S.UserAvatar>
            </Tooltip>
        );
    }
}

